/**
 * Parses SRT subtitle content.
 * @param {string} srtContent - The raw SRT content as a string.
 * @returns {Array<object>} An array of subtitle objects, each with startTime, endTime, and text properties.
 *          Example: [{ startTime: 1.0, endTime: 3.5, text: "Hello world" }, ...]
 */
export function parseSRT(srtContent) {
  if (!srtContent) {
    return [];
  }

  // Normalize line breaks and split into blocks based on double line breaks
  const blocks = srtContent.trim().replace(/\r\n/g, '\n').split(/\n\n+/);
  const subtitles = [];

  for (const block of blocks) {
    const lines = block.split('\n');
    if (lines.length < 2) continue; // Skip malformed blocks

    // First line might be index, we skip it if it's purely numeric
    let lineIndex = 0;
    if (/^\d+$/.test(lines[lineIndex])) {
      lineIndex++;
      if (lines.length < lineIndex + 1) continue; // Malformed if only index exists
    }

    const timeLine = lines[lineIndex++];
    const textLines = lines.slice(lineIndex);

    const timeMatch = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3}) --> (\d{2}:\d{2}:\d{2},\d{3})/);
    if (timeMatch && textLines.length > 0) {
      const startTime = parseTime(timeMatch[1]);
      const endTime = parseTime(timeMatch[2]);
      const text = textLines.join('\n').trim(); // Join multi-line text

      if (startTime !== null && endTime !== null && text) {
         subtitles.push({ startTime, endTime, text });
      }
    } else {
        console.warn("Skipping malformed SRT block:", block);
    }
  }

  // Sort subtitles by start time just in case they are out of order
  subtitles.sort((a, b) => a.startTime - b.startTime);

  return subtitles;
}

// Helper function to parse SRT time format HH:MM:SS,ms into seconds
function parseTime(timeString) {
  if (!timeString) return null; // Return null for invalid input
  const parts = timeString.split(':');
  if (parts.length !== 3) return null;
  const secondsParts = parts[2].split(',');
  if (secondsParts.length !== 2) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(secondsParts[0], 10);
  const milliseconds = parseInt(secondsParts[1], 10);

  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds) || isNaN(milliseconds)) {
      return null; // Invalid number parsing
  }

  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
} 